/**
 * Chat Cache Utility using IndexedDB
 * Provides instant message loading and offline support
 */

const DB_NAME = 'privluxe_chat_cache';
const DB_VERSION = 1;
const CACHE_EXPIRY_DAYS = 7;

let db = null;

// Initialize IndexedDB
export const initChatCache = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[ChatCache] Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('[ChatCache] IndexedDB initialized');
      
      // Cleanup expired data on init
      cleanupExpiredData();
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Store for messages
      if (!database.objectStoreNames.contains('messages')) {
        const messagesStore = database.createObjectStore('messages', { keyPath: 'id' });
        messagesStore.createIndex('conversation_id', 'conversation_id', { unique: false });
        messagesStore.createIndex('created_at', 'created_at', { unique: false });
        messagesStore.createIndex('cached_at', 'cached_at', { unique: false });
      }

      // Store for conversations
      if (!database.objectStoreNames.contains('conversations')) {
        const conversationsStore = database.createObjectStore('conversations', { keyPath: 'id' });
        conversationsStore.createIndex('updated_at', 'updated_at', { unique: false });
        conversationsStore.createIndex('cached_at', 'cached_at', { unique: false });
      }

      // Store for pending messages (optimistic UI)
      if (!database.objectStoreNames.contains('pending_messages')) {
        const pendingStore = database.createObjectStore('pending_messages', { keyPath: 'temp_id' });
        pendingStore.createIndex('conversation_id', 'conversation_id', { unique: false });
      }

      console.log('[ChatCache] Database schema created/upgraded');
    };
  });
};

// Clean up expired data
const cleanupExpiredData = async () => {
  if (!db) return;

  const expiryTime = Date.now() - (CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);

  try {
    // Clean expired messages
    const msgTx = db.transaction('messages', 'readwrite');
    const msgStore = msgTx.objectStore('messages');
    const msgIndex = msgStore.index('cached_at');
    const msgRange = IDBKeyRange.upperBound(expiryTime);
    
    let cursor = await new Promise((resolve, reject) => {
      const request = msgIndex.openCursor(msgRange);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    let deletedCount = 0;
    while (cursor) {
      cursor.delete();
      deletedCount++;
      cursor = await new Promise((resolve, reject) => {
        const request = cursor.continue();
        if (request) {
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        } else {
          resolve(null);
        }
      });
    }

    if (deletedCount > 0) {
      console.log(`[ChatCache] Cleaned up ${deletedCount} expired messages`);
    }
  } catch (error) {
    console.error('[ChatCache] Cleanup error:', error);
  }
};

// Cache messages for a conversation (skip messages that can expire)
export const cacheMessages = async (conversationId, messages) => {
  if (!db || !messages?.length) return;

  try {
    const tx = db.transaction('messages', 'readwrite');
    const store = tx.objectStore('messages');
    const cachedAt = Date.now();

    let cachedCount = 0;
    for (const msg of messages) {
      // Don't cache messages with expires_at (disappearing messages)
      // They should always be fetched fresh from server
      if (msg.expires_at) {
        continue;
      }
      const cachedMsg = { ...msg, conversation_id: conversationId, cached_at: cachedAt };
      store.put(cachedMsg);
      cachedCount++;
    }

    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });

    console.log(`[ChatCache] Cached ${cachedCount} messages for conversation ${conversationId} (skipped ${messages.length - cachedCount} expiring)`);
  } catch (error) {
    console.error('[ChatCache] Failed to cache messages:', error);
  }
};

// Get cached messages for a conversation
export const getCachedMessages = async (conversationId, limit = 30) => {
  if (!db) return null;

  try {
    const tx = db.transaction('messages', 'readonly');
    const store = tx.objectStore('messages');
    const index = store.index('conversation_id');
    
    return new Promise((resolve, reject) => {
      const request = index.getAll(IDBKeyRange.only(conversationId));
      request.onsuccess = () => {
        const messages = request.result || [];
        // Sort by created_at descending and limit
        messages.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        const limited = messages.slice(0, limit).reverse();
        console.log(`[ChatCache] Retrieved ${limited.length} cached messages for ${conversationId}`);
        resolve(limited);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[ChatCache] Failed to get cached messages:', error);
    return null;
  }
};

// Add a single message to cache
export const addMessageToCache = async (conversationId, message) => {
  if (!db || !message) return;

  try {
    const tx = db.transaction('messages', 'readwrite');
    const store = tx.objectStore('messages');
    const cachedMsg = { ...message, conversation_id: conversationId, cached_at: Date.now() };
    store.put(cachedMsg);

    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.error('[ChatCache] Failed to add message to cache:', error);
  }
};

// Update message status in cache (for optimistic UI)
export const updateMessageStatus = async (messageId, status, serverId = null) => {
  if (!db) return;

  try {
    const tx = db.transaction('messages', 'readwrite');
    const store = tx.objectStore('messages');
    
    const request = store.get(messageId);
    request.onsuccess = () => {
      const msg = request.result;
      if (msg) {
        msg.status = status;
        if (serverId) msg.id = serverId;
        store.put(msg);
      }
    };
  } catch (error) {
    console.error('[ChatCache] Failed to update message status:', error);
  }
};

// Cache conversations list
export const cacheConversations = async (conversations) => {
  if (!db || !conversations?.length) return;

  try {
    const tx = db.transaction('conversations', 'readwrite');
    const store = tx.objectStore('conversations');
    const cachedAt = Date.now();

    for (const conv of conversations) {
      const cachedConv = { ...conv, cached_at: cachedAt };
      store.put(cachedConv);
    }

    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });

    console.log(`[ChatCache] Cached ${conversations.length} conversations`);
  } catch (error) {
    console.error('[ChatCache] Failed to cache conversations:', error);
  }
};

// Get cached conversations
export const getCachedConversations = async () => {
  if (!db) return null;

  try {
    const tx = db.transaction('conversations', 'readonly');
    const store = tx.objectStore('conversations');
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const conversations = request.result || [];
        // Sort by updated_at descending
        conversations.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        console.log(`[ChatCache] Retrieved ${conversations.length} cached conversations`);
        resolve(conversations);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[ChatCache] Failed to get cached conversations:', error);
    return null;
  }
};

// Update a single conversation in cache
export const updateConversationInCache = async (conversation) => {
  if (!db || !conversation) return;

  try {
    const tx = db.transaction('conversations', 'readwrite');
    const store = tx.objectStore('conversations');
    const cachedConv = { ...conversation, cached_at: Date.now() };
    store.put(cachedConv);

    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.error('[ChatCache] Failed to update conversation in cache:', error);
  }
};

// Add pending message (optimistic UI)
export const addPendingMessage = async (message) => {
  if (!db) return;

  try {
    const tx = db.transaction('pending_messages', 'readwrite');
    const store = tx.objectStore('pending_messages');
    store.put(message);

    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.error('[ChatCache] Failed to add pending message:', error);
  }
};

// Remove pending message (after server confirms)
export const removePendingMessage = async (tempId) => {
  if (!db) return;

  try {
    const tx = db.transaction('pending_messages', 'readwrite');
    const store = tx.objectStore('pending_messages');
    store.delete(tempId);

    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.error('[ChatCache] Failed to remove pending message:', error);
  }
};

// Get pending messages for a conversation
export const getPendingMessages = async (conversationId) => {
  if (!db) return [];

  try {
    const tx = db.transaction('pending_messages', 'readonly');
    const store = tx.objectStore('pending_messages');
    const index = store.index('conversation_id');
    
    return new Promise((resolve, reject) => {
      const request = index.getAll(IDBKeyRange.only(conversationId));
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[ChatCache] Failed to get pending messages:', error);
    return [];
  }
};

// Clear all cache
export const clearCache = async () => {
  if (!db) return;

  try {
    const tx1 = db.transaction('messages', 'readwrite');
    tx1.objectStore('messages').clear();
    
    const tx2 = db.transaction('conversations', 'readwrite');
    tx2.objectStore('conversations').clear();
    
    const tx3 = db.transaction('pending_messages', 'readwrite');
    tx3.objectStore('pending_messages').clear();

    console.log('[ChatCache] Cache cleared');
  } catch (error) {
    console.error('[ChatCache] Failed to clear cache:', error);
  }
};

// Clear messages for a specific conversation
export const clearConversationCache = async (conversationId) => {
  if (!db || !conversationId) return;

  try {
    const tx = db.transaction('messages', 'readwrite');
    const store = tx.objectStore('messages');
    const index = store.index('conversation_id');
    
    const request = index.openCursor(IDBKeyRange.only(conversationId));
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };

    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });

    console.log(`[ChatCache] Cleared cache for conversation ${conversationId}`);
  } catch (error) {
    console.error('[ChatCache] Failed to clear conversation cache:', error);
  }
};

export default {
  initChatCache,
  cacheMessages,
  getCachedMessages,
  addMessageToCache,
  updateMessageStatus,
  cacheConversations,
  getCachedConversations,
  updateConversationInCache,
  addPendingMessage,
  removePendingMessage,
  getPendingMessages,
  clearCache,
  clearConversationCache
};
